import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { ClipboardList, TrendingUp, TrendingDown, Minus, Plus, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { formatUKDate } from "@/lib/dateUtils";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from "recharts";

interface ClinicalAssessmentsProps {
  clientId: string;
  clinicianId: string;
}

// PHQ-9 Questions
const PHQ9_QUESTIONS = [
  "Little interest or pleasure in doing things",
  "Feeling down, depressed, or hopeless",
  "Trouble falling or staying asleep, or sleeping too much",
  "Feeling tired or having little energy",
  "Poor appetite or overeating",
  "Feeling bad about yourself - or that you are a failure",
  "Trouble concentrating on things",
  "Moving or speaking slowly, or being fidgety/restless",
  "Thoughts that you would be better off dead, or hurting yourself"
];

// GAD-7 Questions
const GAD7_QUESTIONS = [
  "Feeling nervous, anxious or on edge",
  "Not being able to stop or control worrying",
  "Worrying too much about different things",
  "Trouble relaxing",
  "Being so restless that it is hard to sit still",
  "Becoming easily annoyed or irritable",
  "Feeling afraid as if something awful might happen"
];

const RESPONSE_OPTIONS = [
  { value: 0, label: "Not at all" },
  { value: 1, label: "Several days" },
  { value: 2, label: "More than half the days" },
  { value: 3, label: "Nearly every day" }
];

export function ClinicalAssessments({ clientId, clinicianId }: ClinicalAssessmentsProps) {
  const queryClient = useQueryClient();
  const [activeAssessment, setActiveAssessment] = useState<string | null>(null);
  const [responses, setResponses] = useState<number[]>([]);
  const [notes, setNotes] = useState("");

  // Fetch assessments
  const { data: assessments, isLoading } = useQuery({
    queryKey: ['clinical-assessments', clientId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('clinical_assessments')
        .select('*')
        .eq('client_id', clientId)
        .order('administered_at', { ascending: false });
      
      if (error) throw error;
      return data;
    }
  });

  // Fetch benchmarks
  const { data: benchmarks } = useQuery({
    queryKey: ['assessment-benchmarks'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('assessment_benchmarks')
        .select('*');
      
      if (error) throw error;
      return data;
    }
  });

  // Submit assessment
  const submitMutation = useMutation({
    mutationFn: async ({ type, responses, notes }: { type: string; responses: number[]; notes: string }) => {
      const totalScore = responses.reduce((sum, val) => sum + val, 0);
      const benchmark = benchmarks?.find(b => 
        b.assessment_type === type && 
        totalScore >= b.score_range_min && 
        totalScore <= b.score_range_max
      );

      const { error } = await supabase
        .from('clinical_assessments')
        .insert({
          client_id: clientId,
          clinician_id: clinicianId,
          assessment_type: type,
          responses: responses,
          total_score: totalScore,
          severity_level: benchmark?.severity_label || 'Unknown',
          notes
        });
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinical-assessments', clientId] });
      setActiveAssessment(null);
      setResponses([]);
      setNotes("");
      toast.success("Assessment completed successfully");
    },
    onError: () => {
      toast.error("Failed to submit assessment");
    }
  });

  const startAssessment = (type: string) => {
    const questionCount = type === 'PHQ-9' ? 9 : 7;
    setResponses(new Array(questionCount).fill(-1));
    setActiveAssessment(type);
  };

  const getSeverityColour = (severity: string) => {
    switch (severity?.toLowerCase()) {
      case 'minimal': return 'bg-green-500/20 text-green-700 border-green-500/30';
      case 'mild': return 'bg-yellow-500/20 text-yellow-700 border-yellow-500/30';
      case 'moderate': return 'bg-orange-500/20 text-orange-700 border-orange-500/30';
      case 'moderately severe': return 'bg-red-500/20 text-red-700 border-red-500/30';
      case 'severe': return 'bg-red-600/20 text-red-800 border-red-600/30';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getQuestions = (type: string) => {
    return type === 'PHQ-9' ? PHQ9_QUESTIONS : GAD7_QUESTIONS;
  };

  const getTrendIcon = (current: number, previous: number) => {
    if (current < previous) return <TrendingDown className="h-4 w-4 text-green-600" />;
    if (current > previous) return <TrendingUp className="h-4 w-4 text-red-600" />;
    return <Minus className="h-4 w-4 text-muted-foreground" />;
  };

  // Prepare chart data
  const chartData = (type: string) => {
    return assessments
      ?.filter(a => a.assessment_type === type)
      ?.slice(0, 10)
      ?.reverse()
      ?.map(a => ({
        date: formatUKDate(new Date(a.administered_at)),
        score: a.total_score
      })) || [];
  };

  if (activeAssessment) {
    const questions = getQuestions(activeAssessment);
    const allAnswered = responses.every(r => r >= 0);

    return (
      <Card className="bg-card/50 backdrop-blur-sm border-border/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-primary" />
            {activeAssessment} Assessment
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <p className="text-sm text-muted-foreground">
            Over the last 2 weeks, how often have you been bothered by any of the following problems?
          </p>

          <div className="space-y-4">
            {questions.map((question, idx) => (
              <div key={idx} className="p-4 rounded-lg bg-muted/30 border border-border/30">
                <p className="text-sm font-medium mb-3">{idx + 1}. {question}</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {RESPONSE_OPTIONS.map(option => (
                    <Button
                      key={option.value}
                      variant={responses[idx] === option.value ? "default" : "outline"}
                      size="sm"
                      className="text-xs"
                      onClick={() => {
                        const newResponses = [...responses];
                        newResponses[idx] = option.value;
                        setResponses(newResponses);
                      }}
                    >
                      {option.label}
                    </Button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Clinical Notes</label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any clinical observations..."
              className="bg-background/50"
            />
          </div>

          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setActiveAssessment(null)}>
              Cancel
            </Button>
            <Button
              onClick={() => submitMutation.mutate({ type: activeAssessment, responses, notes })}
              disabled={!allAnswered || submitMutation.isPending}
            >
              Submit Assessment
            </Button>
          </div>

          {allAnswered && (
            <div className="p-4 rounded-lg bg-primary/10 border border-primary/20">
              <p className="text-sm font-medium">
                Preliminary Score: {responses.reduce((sum, val) => sum + val, 0)}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-card/50 backdrop-blur-sm border-border/30">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5 text-primary" />
            Clinical Assessments
          </CardTitle>
          <div className="flex gap-2">
            <Button size="sm" onClick={() => startAssessment('PHQ-9')}>
              <Plus className="h-4 w-4 mr-1" />
              PHQ-9
            </Button>
            <Button size="sm" variant="outline" onClick={() => startAssessment('GAD-7')}>
              <Plus className="h-4 w-4 mr-1" />
              GAD-7
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="history">
          <TabsList className="mb-4">
            <TabsTrigger value="history">Assessment History</TabsTrigger>
            <TabsTrigger value="trends">Score Trends</TabsTrigger>
          </TabsList>

          <TabsContent value="history" className="space-y-3">
            {isLoading ? (
              <p className="text-muted-foreground text-sm">Loading assessments...</p>
            ) : assessments?.length === 0 ? (
              <p className="text-muted-foreground text-sm">No assessments completed yet</p>
            ) : (
              assessments?.map((assessment, idx) => {
                const previous = assessments[idx + 1];
                const maxScore = assessment.assessment_type === 'PHQ-9' ? 27 : 21;
                
                return (
                  <div
                    key={assessment.id}
                    className="p-4 rounded-lg bg-muted/30 border border-border/30"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">{assessment.assessment_type}</Badge>
                        <span className="text-sm text-muted-foreground">
                          {formatUKDate(new Date(assessment.administered_at))}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        {previous && getTrendIcon(assessment.total_score, previous.total_score)}
                        <span className="font-semibold">{assessment.total_score}/{maxScore}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Progress value={(assessment.total_score / maxScore) * 100} className="flex-1" />
                      <Badge className={getSeverityColour(assessment.severity_level)}>
                        {assessment.severity_level}
                      </Badge>
                    </div>
                    {assessment.notes && (
                      <p className="text-xs text-muted-foreground mt-2">{assessment.notes}</p>
                    )}
                  </div>
                );
              })
            )}
          </TabsContent>

          <TabsContent value="trends">
            <div className="space-y-6">
              {['PHQ-9', 'GAD-7'].map(type => {
                const data = chartData(type);
                if (data.length === 0) return null;
                
                return (
                  <div key={type} className="space-y-2">
                    <h4 className="text-sm font-medium">{type} Score Over Time</h4>
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={data}>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                          <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                          <YAxis domain={[0, type === 'PHQ-9' ? 27 : 21]} tick={{ fontSize: 10 }} stroke="hsl(var(--muted-foreground))" />
                          <Tooltip />
                          <Line
                            type="monotone"
                            dataKey="score"
                            stroke="hsl(var(--primary))"
                            strokeWidth={2}
                            dot={{ fill: "hsl(var(--primary))" }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                );
              })}
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
}
